import { axiosInstance as axios } from "./axiosManager"

const API_URL = process.env.REACT_APP_HOST_API + "/labeling"

// ============================================
// LABELING SESSION API
// ============================================

/**
 * Get or create a labeling session for a specific student/labeler combination
 */
export const getOrCreateLabelingSession = async (sessionId, studentUserId, labelerName, batchId) => {
  try {
    const response = await axios.post(`${API_URL}/sessions`, {
      sessionId,
      studentUserId,
      labelerName,
      batchId
    })
    return response.data.labelingSession
  } catch (error) {
    console.error("Error getting/creating labeling session:", error)
    throw error
  }
}

/**
 * Load full labeling data for a session (tags, patterns, assessments, query labels)
 */
export const loadLabelingSession = async (labelingSessionId) => {
  try {
    const response = await axios.get(`${API_URL}/sessions/${labelingSessionId}`)
    return response.data
    // Returns: { session, tags: [], patterns: [{ ...pattern, assessment, behaviors }], queryLabels: [] }
  } catch (error) {
    console.error("Error loading labeling session:", error)
    throw error
  }
}

/**
 * Save all labeling data at once (bulk save)
 */
export const saveAllLabelingData = async (labelingSessionId, data) => {
  try {
    const response = await axios.put(`${API_URL}/sessions/${labelingSessionId}/save`, data)
    return response.data
  } catch (error) {
    console.error("Error saving labeling data:", error)
    throw error
  }
}

/**
 * Complete a labeling session (submit)
 */
export const completeLabelingSession = async (labelingSessionId, submitData) => {
  try {
    const response = await axios.put(`${API_URL}/sessions/${labelingSessionId}/complete`, submitData)
    return response.data.labelingSession
  } catch (error) {
    console.error("Error completing labeling session:", error)
    throw error
  }
}

/**
 * Update labeling session metadata (notes, flagged, etc.)
 */
export const updateLabelingSession = async (labelingSessionId, updateData) => {
  try {
    const response = await axios.put(`${API_URL}/sessions/${labelingSessionId}`, updateData)
    return response.data.labelingSession
  } catch (error) {
    console.error("Error updating labeling session:", error)
    throw error
  }
}

// ============================================
// QUEUE API
// ============================================

/**
 * Get labeling status for all students in a session for a specific labeler
 */
export const getLabelerQueue = async (labelerName, sessionId) => {
  try {
    const response = await axios.get(`${API_URL}/queue/${encodeURIComponent(labelerName)}/${sessionId}`)
    return response.data.sessions
  } catch (error) {
    console.error("Error getting labeler queue:", error)
    return []
  }
}

/**
 * Get labeling status for multiple students at once
 */
export const getLabelingStatusForStudents = async (labelerName, sessionId, studentUserIds) => {
  try {
    const response = await axios.post(`${API_URL}/queue/status`, {
      labelerName,
      sessionId,
      studentUserIds
    })
    return response.data.statusMap
  } catch (error) {
    console.error("Error getting labeling status:", error)
    return {}
  }
}

// ============================================
// BEHAVIORAL TAG API
// ============================================

/**
 * Create a behavioral tag
 */
export const createBehavioralTag = async (labelingSessionId, tagData) => {
  try {
    const response = await axios.post(`${API_URL}/sessions/${labelingSessionId}/tags`, tagData)
    return response.data.tag
  } catch (error) {
    console.error("Error creating behavioral tag:", error)
    throw error
  }
}

/**
 * Delete a behavioral tag
 */
export const deleteBehavioralTag = async (labelingSessionId, tagId) => {
  try {
    await axios.delete(`${API_URL}/sessions/${labelingSessionId}/tags/${tagId}`)
    return true
  } catch (error) {
    console.error("Error deleting behavioral tag:", error)
    throw error
  }
}

/**
 * Get all tags for a session
 */
export const getTagsForSession = async (labelingSessionId) => {
  try {
    const response = await axios.get(`${API_URL}/sessions/${labelingSessionId}/tags`)
    return response.data.tags
  } catch (error) {
    console.error("Error getting tags:", error)
    return []
  }
}

// ============================================
// PATTERN API
// ============================================

/**
 * Save a pattern (create or update)
 */
export const savePattern = async (labelingSessionId, patternData) => {
  try {
    const response = await axios.post(`${API_URL}/sessions/${labelingSessionId}/patterns`, patternData)
    return response.data.pattern
  } catch (error) {
    console.error("Error saving pattern:", error)
    throw error
  }
}

/**
 * Delete a pattern
 */
export const deletePattern = async (labelingSessionId, patternId) => {
  try {
    await axios.delete(`${API_URL}/sessions/${labelingSessionId}/patterns/${patternId}`)
    return true
  } catch (error) {
    console.error("Error deleting pattern:", error)
    throw error
  }
}

/**
 * Update pattern assessment
 */
export const updatePatternAssessment = async (labelingSessionId, patternId, assessmentData) => {
  try {
    const response = await axios.put(
      `${API_URL}/sessions/${labelingSessionId}/patterns/${patternId}/assessment`,
      assessmentData
    )
    return response.data.assessment
  } catch (error) {
    console.error("Error updating assessment:", error)
    throw error
  }
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Transform tags from DB format to frontend format
 */
export function transformTagsFromDB(dbTags) {
  return dbTags.map(tag => ({
    id: tag.id,
    dbId: tag.id,
    behavior: {
      id: tag.behavior_id,
      label: tag.behavior_label,
      color: tag.behavior_color,
      isCustom: tag.is_custom
    },
    startTime: tag.start_time,
    endTime: tag.end_time,
    confidence: tag.confidence,
    thinkingSubcategory: tag.thinking_subcategory ?? null 
  }))
}

/**
 * Transform patterns from DB format to frontend format
 */
export function transformPatternsFromDB(dbPatterns) {
  return dbPatterns.map(pattern => ({
    id: pattern.id,
    dbId: pattern.id,
    name: pattern.name,
    description: pattern.description,
    startTime: pattern.start_time,
    endTime: pattern.end_time,
    color: pattern.color,
    isPreQuery: pattern.is_pre_query || false,
    behaviors: pattern.behaviors?.map(b => ({
      id: b.id,
      dbId: b.id,
      behavior: {
        id: b.behavior_id,
        label: b.behavior_label,
        color: b.behavior_color
      },
      startTime: b.start_time,
      endTime: b.end_time,
      overlapStart: b.overlap_start,
      overlapEnd: b.overlap_end
    })) || [],
    behaviorSummary: calculateBehaviorSummary(pattern.behaviors || [], pattern.start_time, pattern.end_time),
    assessment: pattern.assessment ? {
      productivity: pattern.assessment.productivity,
      interventionRecommendation: pattern.assessment.intervention_recommendation,
      annotatorConfidence: pattern.assessment.annotator_confidence,
      isComplete: pattern.assessment.is_complete
    } : null
  }))
}

/**
 * Calculate behavior summary from behaviors list
 */
function calculateBehaviorSummary(behaviors, patternStart, patternEnd) {
  const summary = {}
  
  behaviors.forEach(b => {
    const behaviorId = b.behavior_id
    const duration = b.overlap_end - b.overlap_start
    
    if (!summary[behaviorId]) {
      summary[behaviorId] = {
        behaviorId: behaviorId,
        behaviorLabel: b.behavior_label,
        behaviorColor: b.behavior_color,
        totalDuration: 0,
        count: 0,
        earliestStart: b.overlap_start
      }
    }
    
    summary[behaviorId].totalDuration += duration
    summary[behaviorId].count += 1
    
    if (b.overlap_start < summary[behaviorId].earliestStart) {
      summary[behaviorId].earliestStart = b.overlap_start
    }
  })
  
  return Object.values(summary).sort((a, b) => a.earliestStart - b.earliestStart)
}

/**
 * Transform tags from frontend format to DB format for saving
 */
export function transformTagsForDB(frontendTags) {
  return frontendTags.map(tag => ({
    id: tag.id,
    dbId: tag.dbId,
    behavior: tag.behavior,
    startTime: tag.startTime,
    endTime: tag.endTime,
    confidence: tag.confidence
  }))
}

/**
 * Transform patterns from frontend format to DB format for saving
 */
export function transformPatternsForDB(frontendPatterns, tagIdMap = {}) {
  return frontendPatterns.map(pattern => ({
    id: pattern.id,
    dbId: pattern.dbId,
    name: pattern.name,
    description: pattern.description,
    startTime: pattern.startTime,
    endTime: pattern.endTime,
    color: pattern.color,
    isPreQuery: pattern.isPreQuery || false,
    behaviors: pattern.behaviors?.map(b => ({
      id: b.id,
      dbId: b.dbId || tagIdMap[b.id],
      overlapStart: b.overlapStart,
      overlapEnd: b.overlapEnd
    })) || [],
    assessment: pattern.assessment
  }))
}

/**
 * Transform query labels from DB format to frontend format
 * DB returns: [{ query_id, label_id, query_index, query_timestamp, query_text }, ...]
 * Frontend expects: { [queryId]: labelId, ... }
 */
export function transformQueryLabelsFromDB(dbQueryLabels) {
  if (!dbQueryLabels || dbQueryLabels.length === 0) return {}
  
  const queryLabels = {}
  dbQueryLabels.forEach(ql => {
    queryLabels[ql.query_id] = ql.label_id
  })
  
  return queryLabels
}

/**
 * Transform query labels from frontend format to DB format for saving
 * Frontend has: { [queryId]: labelId, ... }
 * Also needs query metadata from chatQueries array
 */
export function transformQueryLabelsForDB(queryLabels, chatQueries = []) {
  if (!queryLabels || Object.keys(queryLabels).length === 0) return {}
  
  // Build a lookup map from chatQueries for metadata
  const queryMetaMap = {}
  chatQueries.forEach(q => {
    queryMetaMap[q.id] = {
      queryIndex: q.index,
      queryTimestamp: q.timestamp,
      queryText: q.text
    }
  })
  
  // Transform to include metadata
  const enrichedLabels = {}
  Object.entries(queryLabels).forEach(([queryId, labelId]) => {
    const meta = queryMetaMap[queryId] || {}
    enrichedLabels[queryId] = {
      labelId,
      queryIndex: meta.queryIndex,
      queryTimestamp: meta.queryTimestamp,
      queryText: meta.queryText
    }
  })
  
  return enrichedLabels
}

/**
 * Get labeler counts for all students in a session
 */
export const getLabelerCountsForSession = async (sessionId) => {
  try {
    const response = await axios.get(`${API_URL}/irr/counts/${sessionId}`)
    return response.data.counts
  } catch (error) {
    console.error("Error getting labeler counts:", error)
    return {}
  }
}

/**
 * Get IRR comparison data for a specific student session
 */
export const getIRRComparisonData = async (sessionId, studentUserId) => {
  try {
    const response = await axios.get(`${API_URL}/irr/compare/${sessionId}/${studentUserId}`)
    return response.data
  } catch (error) {
    console.error("Error getting IRR comparison data:", error)
    throw error
  }
}

/**
 * Get ground truth segments for a student session
 */
export async function getGroundTruth(vispiSessionId, studentUserId) {
  const response = await axios.get(
    `${API_URL}/ground-truth/${vispiSessionId}/${studentUserId}`
  )
  return response.data
}

/**
 * Save ground truth segments for a student session
 */
export async function saveGroundTruth(vispiSessionId, studentUserId, slicePoints) {
  const response = await axios.put(
    `${API_URL}/ground-truth/${vispiSessionId}/${studentUserId}`,
    { slicePoints }
  )
  return response.data
}

/**
 * Transform profile labels from DB format to frontend format
 * DB returns: [{ query_id, profile_id, window_type, window_start, window_end, ... }, ...]
 * Frontend expects: { [queryId]: profileId, ... }
 */
export function transformProfileLabelsFromDB(dbProfileLabels) {
  if (!dbProfileLabels || dbProfileLabels.length === 0) return {}
  
  const profileLabels = {}
  dbProfileLabels.forEach(pl => {
    profileLabels[pl.query_id] = pl.profile_id
  })
  
  return profileLabels
}